import React, { useEffect, useMemo, useState } from "react";
// import RightSideDrawer from "../../../../components/reusable/RightSideDrawer";
// import LoadingSpinner from "../../../../components/loaders/LoadingSpinner";
// import StatusBadge from "../../../../utils/StatusBadge";
// import DrawerSection from "../../../../components/reusable/DrawerSection";
// import DetailRow from "../../../../components/reusable/DetailRow";
// import { formatDate, formatDateTime, formatPlatformName } from "../../../../utils/formatter";
import BaseIcon from "../../../../../components/svg_icons/BaseIcon";
import "../../../../../css/pages/ca_pages/models/client_followups/ViewDocRequest.css";

/*
 * ---------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------
 */

const TIMEZONES = [
  {
    value: "Asia/Kolkata",
    label: "India Standard Time (Asia/Kolkata)",
  },
  {
    value: "Asia/Dubai",
    label: "Gulf Standard Time (Asia/Dubai)",
  },
  {
    value: "Asia/Singapore",
    label: "Singapore Time (Asia/Singapore)",
  },
  {
    value: "Europe/London",
    label: "UK Time (Europe/London)",
  },
  {
    value: "Europe/Berlin",
    label: "Central European Time (Europe/Berlin)",
  },
  {
    value: "America/New_York",
    label: "Eastern Time (America/New_York)",
  },
  {
    value: "America/Chicago",
    label: "Central Time (America/Chicago)",
  },
  {
    value: "America/Denver",
    label: "Mountain Time (America/Denver)",
  },
  {
    value: "America/Los_Angeles",
    label: "Pacific Time (America/Los_Angeles)",
  },
];

const CHANNELS = [
  {
    value: "whatsapp",
    label: "WhatsApp",
    icon: "◉",
  },
  {
    value: "email",
    label: "Email",
    icon: "✉",
  },
  {
    value: "sms",
    label: "SMS",
    icon: "▣",
    comingSoon: true,
  },
];

const WHATSAPP_TEMPLATES = [
  {
    value: "document_request_reminder",
    label: "Document request reminder",
    description: "Standard reminder with the document upload link.",
  },
  {
    value: "reminder_with_custom_message_with_button",
    label: "Custom message + upload button",
    description: "Custom message with a direct upload action.",
  },
  {
    value: "reminder_with_message",
    label: "Custom message",
    description: "Custom WhatsApp reminder without an upload button.",
  },
];

const EMAIL_TEMPLATES = [
  {
    value: "document_request",
    label: "Document request",
    description: "Standard document request email.",
  },
];

/*
 * ---------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------------
 */

const createId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const formatDateTimeForInput = (value) => {
  if (!value) {
    return "";
  }

  /*
   * Backend may return:
   *
   * 2026-09-30T18:00:00
   * 2026-09-30T18:00:00+05:30
   * 2026-09-30T18:00:00Z
   */

  const stringValue = String(value);

  if (stringValue.length >= 16) {
    return stringValue.slice(0, 16);
  }

  return stringValue;
};

const splitScheduledDateTime = (value) => {
  const formatted = formatDateTimeForInput(value);

  if (!formatted) {
    return {
      date: "",
      time: "09:00",
    };
  }

  const [date, time] = formatted.split("T");

  return {
    date: date || "",
    time: time || "09:00",
  };
};

const buildScheduledDateTime = (date, time) => {
  if (!date) {
    return "";
  }

  return `${date}T${time || "09:00"}:00`;
};

const createDelivery = (platform) => {
  if (platform === "whatsapp") {
    return {
      id: createId(),
      platform: "whatsapp",
      template_key: "reminder_with_custom_message_with_button",
      custom_subject: "",
      custom_message: "",
      template_data: {},
      is_enabled: true,
    };
  }

  if (platform === "email") {
    return {
      id: createId(),
      platform: "email",
      template_key: "document_request",
      custom_subject: "",
      custom_message: "",
      template_data: {},
      is_enabled: true,
    };
  }

  return {
    id: createId(),
    platform,
    template_key: "",
    custom_subject: "",
    custom_message: "",
    template_data: {},
    is_enabled: true,
  };
};

const normalizeDelivery = (delivery) => ({
  ...createDelivery(delivery?.platform || "whatsapp"),
  ...delivery,
  id: delivery?.id || createId(),
  platform: delivery?.platform || "whatsapp",
  custom_subject: delivery?.custom_subject || "",
  custom_message: delivery?.custom_message || "",
  template_data:
    delivery?.template_data &&
    typeof delivery.template_data === "object"
      ? delivery.template_data
      : {},
  is_enabled: delivery?.is_enabled !== false,
});

const normalizeReminder = (reminder, index, fallbackTimezone) => {
  const schedule = splitScheduledDateTime(reminder?.scheduled_for);

  const deliveries = Array.isArray(reminder?.deliveries)
    ? reminder.deliveries.map(normalizeDelivery)
    : [];

  return {
    id: reminder?.id || createId(),

    /*
     * Backend identity is not sent back during update.
     * This ID is only for React rendering.
     */
    reminderNumber:
      Number(reminder?.reminder_number) || Number(reminder?.reminderNumber) || index + 1,

    date: reminder?.date || schedule.date,
    time: reminder?.time || schedule.time,

    timezone:
      reminder?.timezone ||
      fallbackTimezone ||
      "Asia/Kolkata",

    deliveries,

    is_paused: reminder?.is_paused === true,
    is_completed: reminder?.is_completed === true,

    next_run_at: reminder?.next_run_at || null,
    last_run_at: reminder?.last_run_at || null,
  };
};

const normalizeRequest = (request) => {
  const timezone =
    request?.timezone ||
    request?.default_timezone ||
    "Asia/Kolkata";

  const rawReminders = Array.isArray(request?.reminder_schedules)
    ? request.reminder_schedules
    : [];

  return {
    title: request?.title || "",
    documentType: request?.document_type || "",
    dueDate: formatDateTimeForInput(request?.due_date),
    description: request?.description || "",

    requestExpiryDays:
      request?.request_expiry_days ??
      30,

    stopAfterSubmission:
      request?.stop_reminders_after_submission !== false,

    skipWeekends:
      request?.skip_weekends !== false,

    timezone,

    reminders: rawReminders.map((reminder, index) =>
      normalizeReminder(reminder, index, timezone),
    ),
  };
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return "Unable to update document request.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (typeof data.detail === "string") {
    return data.detail;
  }

  if (typeof data === "object") {
    const messages = [];

    Object.entries(data).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        value.forEach((message) => {
          messages.push(`${field}: ${message}`);
        });
      } else if (typeof value === "string") {
        messages.push(`${field}: ${value}`);
      }
    });

    if (messages.length) {
      return messages.join(" ");
    }
  }

  return "Unable to update document request.";
};

/*
 * ---------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------
 */

const ViewDocRequest = ({
  isOpen,
  loading = false,
  request,
  onClose,
  onUpdate,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(null);

  /*
   * -------------------------------------------------------------------------
   * Load request into local editor
   * -------------------------------------------------------------------------
   */

  useEffect(() => {
    if (!request) {
      setFormData(null);
      setEditing(false);
      setError("");
      return;
    }

    setFormData(normalizeRequest(request));
    setEditing(false);
    setError("");
  }, [request]);

  /*
   * -------------------------------------------------------------------------
   * Derived values
   * -------------------------------------------------------------------------
   */

  const reminders = useMemo(
    () => (Array.isArray(formData?.reminders) ? formData.reminders : []),
    [formData],
  );

  /*
   * -------------------------------------------------------------------------
   * Basic form updates
   * -------------------------------------------------------------------------
   */

  const updateForm = (updates) => {
    setFormData((previous) => ({
      ...previous,
      ...updates,
    }));

    if (error) {
      setError("");
    }
  };

  /*
   * -------------------------------------------------------------------------
   * Reminder operations
   * -------------------------------------------------------------------------
   */

  const addReminder = () => {
    const nextNumber = reminders.length + 1;

    const reminder = {
      id: createId(),
      reminderNumber: nextNumber,
      date: "",
      time: "09:00",
      timezone: formData?.timezone || "Asia/Kolkata",
      deliveries: [createDelivery("whatsapp")],
      is_paused: false,
      is_completed: false,
    };

    updateForm({
      reminders: [...reminders, reminder],
    });
  };

  const removeReminder = (reminderId) => {
    const remaining = reminders
      .filter((reminder) => reminder.id !== reminderId)
      .map((reminder, index) => ({
        ...reminder,
        reminderNumber: index + 1,
      }));

    updateForm({
      reminders: remaining,
    });
  };

  const updateReminder = (reminderId, updates) => {
    updateForm({
      reminders: reminders.map((reminder) =>
        reminder.id === reminderId
          ? {
              ...reminder,
              ...updates,
            }
          : reminder,
      ),
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Delivery operations
   * -------------------------------------------------------------------------
   */

  const addDelivery = (reminderId, platform) => {
    const reminder = reminders.find(
      (item) => item.id === reminderId,
    );

    if (!reminder) {
      return;
    }

    const existing = Array.isArray(reminder.deliveries)
      ? reminder.deliveries
      : [];

    if (
      existing.some(
        (delivery) => delivery.platform === platform,
      )
    ) {
      return;
    }

    updateReminder(reminderId, {
      deliveries: [
        ...existing,
        createDelivery(platform),
      ],
    });
  };

  const removeDelivery = (reminderId, deliveryId) => {
    const reminder = reminders.find(
      (item) => item.id === reminderId,
    );

    if (!reminder) {
      return;
    }

    updateReminder(reminderId, {
      deliveries: reminder.deliveries.filter(
        (delivery) => delivery.id !== deliveryId,
      ),
    });
  };

  const updateDelivery = (
    reminderId,
    deliveryId,
    updates,
  ) => {
    const reminder = reminders.find(
      (item) => item.id === reminderId,
    );

    if (!reminder) {
      return;
    }

    updateReminder(reminderId, {
      deliveries: reminder.deliveries.map((delivery) =>
        delivery.id === deliveryId
          ? {
              ...delivery,
              ...updates,
            }
          : delivery,
      ),
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Validation
   * -------------------------------------------------------------------------
   */

  const validateForm = () => {
    if (!formData) {
      return "No document request loaded.";
    }

    if (!formData.title.trim()) {
      return "Request title is required.";
    }

    if (!formData.documentType.trim()) {
      return "Document type is required.";
    }

    if (!formData.dueDate) {
      return "Submission deadline is required.";
    }

    const expiryDays = Number(
      formData.requestExpiryDays,
    );

    if (
      !Number.isFinite(expiryDays) ||
      expiryDays < 1
    ) {
      return "Request expiry must be at least 1 day.";
    }

    for (
      let index = 0;
      index < reminders.length;
      index += 1
    ) {
      const reminder = reminders[index];

      if (!reminder.date) {
        return `Please select a date for Reminder ${index + 1}.`;
      }

      if (!reminder.time) {
        return `Please select a time for Reminder ${index + 1}.`;
      }

      const deliveries = Array.isArray(
        reminder.deliveries,
      )
        ? reminder.deliveries
        : [];

      if (deliveries.length === 0) {
        return `Reminder ${index + 1} must have at least one delivery channel.`;
      }

      const enabledDeliveries = deliveries.filter(
        (delivery) =>
          delivery.is_enabled !== false,
      );

      if (enabledDeliveries.length === 0) {
        return `Reminder ${index + 1} must have at least one enabled delivery channel.`;
      }

      for (
        let deliveryIndex = 0;
        deliveryIndex < deliveries.length;
        deliveryIndex += 1
      ) {
        const delivery =
          deliveries[deliveryIndex];

        if (!delivery.platform) {
          return `Reminder ${index + 1} contains an invalid delivery channel.`;
        }

        if (
          delivery.is_enabled !== false &&
          !delivery.template_key
        ) {
          return `Please select a template for ${delivery.platform} in Reminder ${index + 1}.`;
        }
      }
    }

    return "";
  };

  /*
   * -------------------------------------------------------------------------
   * Build backend payload
   * -------------------------------------------------------------------------
   *
   * IMPORTANT:
   *
   * Your backend uses replace_reminders().
   *
   * Therefore the PATCH request must contain the COMPLETE reminders array.
   */

  const buildPayload = () => {
    return {
      title: formData.title.trim(),

      document_type:
        formData.documentType.trim(),

      due_date: formData.dueDate
        ? `${formData.dueDate}:00`
        : "",

      description:
        formData.description.trim(),

      request_expiry_days:
        Number(formData.requestExpiryDays) || 30,

      stop_reminders_after_submission:
        formData.stopAfterSubmission !== false,

      skip_weekends:
        formData.skipWeekends !== false,

      reminders: reminders.map(
        (reminder, index) => ({
          reminder_number:
            Number(reminder.reminderNumber) ||
            index + 1,

          scheduled_for:
            buildScheduledDateTime(
              reminder.date,
              reminder.time,
            ),

          timezone:
            reminder.timezone ||
            formData.timezone ||
            "Asia/Kolkata",

          deliveries:
            reminder.deliveries.map(
              (delivery) => ({
                platform:
                  delivery.platform,

                template_key:
                  delivery.template_key || "",

                custom_subject:
                  delivery.custom_subject || "",

                custom_message:
                  delivery.custom_message || "",

                template_data:
                  delivery.template_data &&
                  typeof delivery.template_data ===
                    "object"
                    ? delivery.template_data
                    : {},

                is_enabled:
                  delivery.is_enabled !== false,
              }),
            ),
        }),
      ),
    };
  };

  /*
   * -------------------------------------------------------------------------
   * Save
   * -------------------------------------------------------------------------
   */

  const handleSave = async () => {
    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!request?.client || !request?.id) {
      setError(
        "Unable to identify this document request.",
      );
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = buildPayload();

      const updatedRequest =
        await onUpdate(
          request.client,
          request.id,
          payload,
        );

      /*
       * Parent already updates the drawer with
       * the backend response.
       */

      if (updatedRequest) {
        setFormData(
          normalizeRequest(updatedRequest),
        );
      }

      setEditing(false);
    } catch (updateError) {
      console.error(
        "Error updating document request:",
        updateError,
      );

      setError(
        getApiErrorMessage(updateError),
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * -------------------------------------------------------------------------
   * Cancel editing
   * -------------------------------------------------------------------------
   */

  const handleCancelEdit = () => {
    if (request) {
      setFormData(
        normalizeRequest(request),
      );
    }

    setError("");
    setEditing(false);
  };

  /*
   * -------------------------------------------------------------------------
   * Render guards
   * -------------------------------------------------------------------------
   */

  if (!isOpen) {
    return null;
  }

  return (
    <div className="view-doc-request-overlay">
      <div className="view-doc-request-drawer">

        {/* ===============================================================
            HEADER
        ================================================================ */}

        <div className="view-doc-request-header">
          <div className="view-doc-request-header__content">

            <div className="view-doc-request-header__icon">
              <BaseIcon name="document" />
            </div>

            <div>
              <h2>
                {editing
                  ? "Edit Document Request"
                  : "Document Request"}
              </h2>

              <p>
                {editing
                  ? "Update the request and control its reminder automation."
                  : "View request details, submissions and reminder activity."}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="view-doc-request-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* ===============================================================
            LOADING
        ================================================================ */}

        {loading && (
          <div className="view-doc-request-loading">
            Loading document request...
          </div>
        )}

        {/* ===============================================================
            ERROR
        ================================================================ */}

        {error && (
          <div
            className="view-doc-request-error"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* ===============================================================
            CONTENT
        ================================================================ */}

        {!loading && formData && (
          <div className="view-doc-request-content">

            {/* ===========================================================
                REQUEST DETAILS
            ============================================================ */}

            <section className="view-doc-request-section">

              <div className="view-doc-request-section__header">
                <div>
                  <h3>Request details</h3>

                  <p>
                    Basic information about the
                    document request.
                  </p>
                </div>

                {!editing && (
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      setError("");
                      setEditing(true);
                    }}
                  >
                    Edit request
                  </button>
                )}
              </div>

              <div className="view-doc-request-fields">

                <div className="view-doc-request-field view-doc-request-field--full">
                  <label>
                    Request title
                  </label>

                  {editing ? (
                    <input
                      type="text"
                      value={formData.title}
                      maxLength={255}
                      onChange={(event) =>
                        updateForm({
                          title:
                            event.target.value,
                        })
                      }
                    />
                  ) : (
                    <div className="view-doc-request-value">
                      {formData.title || "—"}
                    </div>
                  )}
                </div>

                <div className="view-doc-request-field">
                  <label>
                    Document type
                  </label>

                  {editing ? (
                    <input
                      type="text"
                      value={
                        formData.documentType
                      }
                      maxLength={100}
                      onChange={(event) =>
                        updateForm({
                          documentType:
                            event.target.value,
                        })
                      }
                    />
                  ) : (
                    <div className="view-doc-request-value">
                      {formData.documentType ||
                        "—"}
                    </div>
                  )}
                </div>

                <div className="view-doc-request-field">
                  <label>
                    Submission deadline
                  </label>

                  {editing ? (
                    <input
                      type="datetime-local"
                      value={
                        formData.dueDate
                      }
                      onChange={(event) =>
                        updateForm({
                          dueDate:
                            event.target.value,
                        })
                      }
                    />
                  ) : (
                    <div className="view-doc-request-value">
                      {formData.dueDate
                        ? new Date(
                            formData.dueDate,
                          ).toLocaleString()
                        : "—"}
                    </div>
                  )}
                </div>

                <div className="view-doc-request-field view-doc-request-field--full">
                  <label>
                    Instructions
                  </label>

                  {editing ? (
                    <textarea
                      rows={5}
                      value={
                        formData.description
                      }
                      onChange={(event) =>
                        updateForm({
                          description:
                            event.target.value,
                        })
                      }
                    />
                  ) : (
                    <div className="view-doc-request-value view-doc-request-value--multiline">
                      {formData.description ||
                        "No instructions provided."}
                    </div>
                  )}
                </div>

              </div>
            </section>

            {/* ===========================================================
                CLIENT
            ============================================================ */}

            <section className="view-doc-request-section">

              <div className="view-doc-request-section__header">
                <div>
                  <h3>Client</h3>

                  <p>
                    Client associated with this
                    document request.
                  </p>
                </div>
              </div>

              <div className="view-doc-request-client">

                <strong>
                  {request?.client_name ||
                    "—"}
                </strong>

                <span>
                  {request?.client_email ||
                    "—"}
                </span>

              </div>
            </section>

            {/* ===========================================================
                AUTOMATION SETTINGS
            ============================================================ */}

            <section className="view-doc-request-section">

              <div className="view-doc-request-section__header">
                <div>
                  <h3>
                    Request automation
                  </h3>

                  <p>
                    Control expiry and reminder
                    behavior for this request.
                  </p>
                </div>
              </div>

              <div className="view-doc-request-automation">

                <div className="view-doc-request-setting">
                  <div>
                    <strong>
                      Skip weekends
                    </strong>

                    <span>
                      Move weekend reminders to
                      the next working day.
                    </span>
                  </div>

                  {editing ? (
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={
                          formData.skipWeekends !==
                          false
                        }
                        onChange={(event) =>
                          updateForm({
                            skipWeekends:
                              event.target.checked,
                          })
                        }
                      />

                      <span className="toggle__slider" />
                    </label>
                  ) : (
                    <strong>
                      {formData.skipWeekends !==
                      false
                        ? "Enabled"
                        : "Disabled"}
                    </strong>
                  )}
                </div>

                <div className="view-doc-request-setting">
                  <div>
                    <strong>
                      Stop reminders after submission
                    </strong>

                    <span>
                      Stop pending reminders after
                      the client submits.
                    </span>
                  </div>

                  {editing ? (
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={
                          formData.stopAfterSubmission !==
                          false
                        }
                        onChange={(event) =>
                          updateForm({
                            stopAfterSubmission:
                              event.target.checked,
                          })
                        }
                      />

                      <span className="toggle__slider" />
                    </label>
                  ) : (
                    <strong>
                      {formData.stopAfterSubmission !==
                      false
                        ? "Enabled"
                        : "Disabled"}
                    </strong>
                  )}
                </div>

                <div className="view-doc-request-setting">
                  <div>
                    <strong>
                      Request expiry
                    </strong>

                    <span>
                      Number of days before the
                      request expires.
                    </span>
                  </div>

                  {editing ? (
                    <div className="view-doc-request-expiry">
                      <input
                        type="number"
                        min="1"
                        max="3650"
                        value={
                          formData.requestExpiryDays
                        }
                        onChange={(event) =>
                          updateForm({
                            requestExpiryDays:
                              event.target.value,
                          })
                        }
                      />

                      <span>
                        days
                      </span>
                    </div>
                  ) : (
                    <strong>
                      {formData.requestExpiryDays}{" "}
                      days
                    </strong>
                  )}
                </div>

                <div className="view-doc-request-setting">
                  <div>
                    <strong>
                      Default timezone
                    </strong>

                    <span>
                      Default timezone used for
                      reminder schedules.
                    </span>
                  </div>

                  {editing ? (
                    <select
                      value={
                        formData.timezone
                      }
                      onChange={(event) =>
                        updateForm({
                          timezone:
                            event.target.value,
                        })
                      }
                    >
                      {TIMEZONES.map(
                        (timezone) => (
                          <option
                            key={
                              timezone.value
                            }
                            value={
                              timezone.value
                            }
                          >
                            {timezone.label}
                          </option>
                        ),
                      )}
                    </select>
                  ) : (
                    <strong>
                      {
                        TIMEZONES.find(
                          (item) =>
                            item.value ===
                            formData.timezone,
                        )?.label ||
                        formData.timezone
                      }
                    </strong>
                  )}
                </div>

              </div>
            </section>

            {/* ===========================================================
                REMINDERS
            ============================================================ */}

            <section className="view-doc-request-section">

              <div className="view-doc-request-section__header">

                <div>
                  <h3>
                    Reminder automation
                  </h3>

                  <p>
                    Configure exactly when and how
                    the client should be reminded.
                  </p>
                </div>

                {editing && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={addReminder}
                  >
                    + Add reminder
                  </button>
                )}

              </div>

              {reminders.length === 0 && (
                <div className="view-doc-request-empty">
                  <strong>
                    No reminders configured
                  </strong>

                  <span>
                    This request currently has no
                    automated reminders.
                  </span>

                  {editing && (
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={addReminder}
                    >
                      + Add first reminder
                    </button>
                  )}
                </div>
              )}

              <div className="view-doc-request-reminders">

                {reminders.map(
                  (reminder, reminderIndex) => (
                    <div
                      className="view-doc-request-reminder"
                      key={reminder.id}
                    >

                      {/* ------------------------------------------------
                          Reminder header
                      ------------------------------------------------- */}

                      <div className="view-doc-request-reminder__header">

                        <div>
                          <span className="reminder-number">
                            {reminderIndex + 1}
                          </span>

                          <div>
                            <strong>
                              Reminder{" "}
                              {reminderIndex + 1}
                            </strong>

                            <small>
                              {reminder.is_completed
                                ? "Completed"
                                : reminder.is_paused
                                  ? "Paused"
                                  : "Scheduled"}
                            </small>
                          </div>
                        </div>

                        {editing && (
                          <button
                            type="button"
                            className="remove-reminder-btn"
                            onClick={() =>
                              removeReminder(
                                reminder.id,
                              )
                            }
                          >
                            Remove
                          </button>
                        )}

                      </div>

                      {/* ------------------------------------------------
                          Schedule
                      ------------------------------------------------- */}

                      <div className="view-doc-request-reminder__schedule">

                        <div>
                          <label>
                            Date
                          </label>

                          {editing ? (
                            <input
                              type="date"
                              value={
                                reminder.date
                              }
                              onChange={(event) =>
                                updateReminder(
                                  reminder.id,
                                  {
                                    date:
                                      event
                                        .target
                                        .value,
                                  },
                                )
                              }
                            />
                          ) : (
                            <span>
                              {reminder.date ||
                                "—"}
                            </span>
                          )}
                        </div>

                        <div>
                          <label>
                            Time
                          </label>

                          {editing ? (
                            <input
                              type="time"
                              value={
                                reminder.time ||
                                "09:00"
                              }
                              onChange={(event) =>
                                updateReminder(
                                  reminder.id,
                                  {
                                    time:
                                      event
                                        .target
                                        .value,
                                  },
                                )
                              }
                            />
                          ) : (
                            <span>
                              {reminder.time ||
                                "—"}
                            </span>
                          )}
                        </div>

                        <div>
                          <label>
                            Timezone
                          </label>

                          {editing ? (
                            <select
                              value={
                                reminder.timezone
                              }
                              onChange={(event) =>
                                updateReminder(
                                  reminder.id,
                                  {
                                    timezone:
                                      event
                                        .target
                                        .value,
                                  },
                                )
                              }
                            >
                              {TIMEZONES.map(
                                (timezone) => (
                                  <option
                                    key={
                                      timezone.value
                                    }
                                    value={
                                      timezone.value
                                    }
                                  >
                                    {
                                      timezone.label
                                    }
                                  </option>
                                ),
                              )}
                            </select>
                          ) : (
                            <span>
                              {
                                TIMEZONES.find(
                                  (item) =>
                                    item.value ===
                                    reminder.timezone,
                                )?.label ||
                                reminder.timezone
                              }
                            </span>
                          )}
                        </div>

                      </div>

                      {/* ------------------------------------------------
                          Deliveries
                      ------------------------------------------------- */}

                      <div className="view-doc-request-deliveries">

                        <div className="view-doc-request-deliveries__header">
                          <div>
                            <strong>
                              Delivery channels
                            </strong>

                            <small>
                              Configure how this reminder
                              reaches the client.
                            </small>
                          </div>

                          {editing && (
                            <div className="channel-add-menu">
                              {CHANNELS.filter(
                                (channel) =>
                                  !reminder.deliveries.some(
                                    (delivery) =>
                                      delivery.platform ===
                                      channel.value,
                                  ),
                              ).map(
                                (channel) => (
                                  <button
                                    key={
                                      channel.value
                                    }
                                    type="button"
                                    disabled={
                                      channel.comingSoon
                                    }
                                    onClick={() =>
                                      addDelivery(
                                        reminder.id,
                                        channel.value,
                                      )
                                    }
                                  >
                                    +{" "}
                                    {
                                      channel.label
                                    }
                                  </button>
                                ),
                              )}
                            </div>
                          )}
                        </div>

                        {reminder.deliveries.length ===
                          0 && (
                          <div className="view-doc-request-empty">
                            No delivery channels
                            configured.
                          </div>
                        )}

                        {reminder.deliveries.map(
                          (delivery) => {
                            const channel =
                              CHANNELS.find(
                                (item) =>
                                  item.value ===
                                  delivery.platform,
                              );

                            return (
                              <div
                                key={
                                  delivery.id
                                }
                                className={`view-doc-request-delivery ${
                                  delivery.is_enabled ===
                                  false
                                    ? "view-doc-request-delivery--disabled"
                                    : ""
                                }`}
                              >

                                <div className="view-doc-request-delivery__header">

                                  <div>
                                    <span>
                                      {
                                        channel?.icon ||
                                        "•"
                                      }
                                    </span>

                                    <div>
                                      <strong>
                                        {channel?.label ||
                                          delivery.platform}
                                      </strong>

                                      <small>
                                        {delivery.is_enabled ===
                                        false
                                          ? "Disabled"
                                          : "Enabled"}
                                      </small>
                                    </div>
                                  </div>

                                  {editing && (
                                    <div>
                                      <label className="toggle toggle--small">
                                        <input
                                          type="checkbox"
                                          checked={
                                            delivery.is_enabled !==
                                            false
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            updateDelivery(
                                              reminder.id,
                                              delivery.id,
                                              {
                                                is_enabled:
                                                  event
                                                    .target
                                                    .checked,
                                              },
                                            )
                                          }
                                        />

                                        <span className="toggle__slider" />
                                      </label>

                                      <button
                                        type="button"
                                        className="remove-delivery-btn"
                                        onClick={() =>
                                          removeDelivery(
                                            reminder.id,
                                            delivery.id,
                                          )
                                        }
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  )}

                                </div>

                                {/* WhatsApp */}

                                {delivery.platform ===
                                  "whatsapp" && (
                                  <div className="view-doc-request-delivery__body">

                                    <div>
                                      <label>
                                        WhatsApp template
                                      </label>

                                      {editing ? (
                                        <select
                                          value={
                                            delivery.template_key ||
                                            ""
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            updateDelivery(
                                              reminder.id,
                                              delivery.id,
                                              {
                                                template_key:
                                                  event
                                                    .target
                                                    .value,
                                              },
                                            )
                                          }
                                        >
                                          <option value="">
                                            Select template
                                          </option>

                                          {WHATSAPP_TEMPLATES.map(
                                            (
                                              template,
                                            ) => (
                                              <option
                                                key={
                                                  template.value
                                                }
                                                value={
                                                  template.value
                                                }
                                              >
                                                {
                                                  template.label
                                                }
                                              </option>
                                            ),
                                          )}
                                        </select>
                                      ) : (
                                        <span>
                                          {WHATSAPP_TEMPLATES.find(
                                            (
                                              template,
                                            ) =>
                                              template.value ===
                                              delivery.template_key,
                                          )?.label ||
                                            delivery.template_key ||
                                            "—"}
                                        </span>
                                      )}
                                    </div>

                                    <div>
                                      <label>
                                        Custom message
                                      </label>

                                      {editing ? (
                                        <textarea
                                          rows={4}
                                          value={
                                            delivery.custom_message ||
                                            ""
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            updateDelivery(
                                              reminder.id,
                                              delivery.id,
                                              {
                                                custom_message:
                                                  event
                                                    .target
                                                    .value,
                                              },
                                            )
                                          }
                                        />
                                      ) : (
                                        <p>
                                          {delivery.custom_message ||
                                            "No custom message."}
                                        </p>
                                      )}
                                    </div>

                                  </div>
                                )}

                                {/* Email */}

                                {delivery.platform ===
                                  "email" && (
                                  <div className="view-doc-request-delivery__body">

                                    <div>
                                      <label>
                                        Email template
                                      </label>

                                      {editing ? (
                                        <select
                                          value={
                                            delivery.template_key ||
                                            ""
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            updateDelivery(
                                              reminder.id,
                                              delivery.id,
                                              {
                                                template_key:
                                                  event
                                                    .target
                                                    .value,
                                              },
                                            )
                                          }
                                        >
                                          <option value="">
                                            Select template
                                          </option>

                                          {EMAIL_TEMPLATES.map(
                                            (
                                              template,
                                            ) => (
                                              <option
                                                key={
                                                  template.value
                                                }
                                                value={
                                                  template.value
                                                }
                                              >
                                                {
                                                  template.label
                                                }
                                              </option>
                                            ),
                                          )}
                                        </select>
                                      ) : (
                                        <span>
                                          {EMAIL_TEMPLATES.find(
                                            (
                                              template,
                                            ) =>
                                              template.value ===
                                              delivery.template_key,
                                          )?.label ||
                                            delivery.template_key ||
                                            "—"}
                                        </span>
                                      )}
                                    </div>

                                    <div>
                                      <label>
                                        Custom subject
                                      </label>

                                      {editing ? (
                                        <input
                                          type="text"
                                          value={
                                            delivery.custom_subject ||
                                            ""
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            updateDelivery(
                                              reminder.id,
                                              delivery.id,
                                              {
                                                custom_subject:
                                                  event
                                                    .target
                                                    .value,
                                              },
                                            )
                                          }
                                        />
                                      ) : (
                                        <span>
                                          {delivery.custom_subject ||
                                            "No custom subject."}
                                        </span>
                                      )}
                                    </div>

                                    <div>
                                      <label>
                                        Custom message
                                      </label>

                                      {editing ? (
                                        <textarea
                                          rows={5}
                                          value={
                                            delivery.custom_message ||
                                            ""
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            updateDelivery(
                                              reminder.id,
                                              delivery.id,
                                              {
                                                custom_message:
                                                  event
                                                    .target
                                                    .value,
                                              },
                                            )
                                          }
                                        />
                                      ) : (
                                        <p>
                                          {delivery.custom_message ||
                                            "No custom message."}
                                        </p>
                                      )}
                                    </div>

                                  </div>
                                )}

                                {/* SMS */}

                                {delivery.platform ===
                                  "sms" && (
                                  <div className="view-doc-request-coming-soon">
                                    SMS delivery is
                                    currently not
                                    enabled.
                                  </div>
                                )}

                              </div>
                            );
                          },
                        )}

                      </div>

                    </div>
                  ),
                )}

              </div>

            </section>

            {/* ===========================================================
                SUBMISSIONS
            ============================================================ */}

            <section className="view-doc-request-section">

              <div className="view-doc-request-section__header">
                <div>
                  <h3>
                    Submissions
                  </h3>

                  <p>
                    Documents submitted by the
                    client for this request.
                  </p>
                </div>
              </div>

              {Array.isArray(
                request?.submissions,
              ) &&
              request.submissions.length > 0 ? (
                <div className="view-doc-request-submissions">

                  {request.submissions.map(
                    (submission) => (
                      <div
                        key={
                          submission.id
                        }
                        className="view-doc-request-submission"
                      >
                        <div>
                          <strong>
                            {submission.file_name ||
                              submission.name ||
                              "Document submission"}
                          </strong>

                          <span>
                            {submission.uploaded_by_name ||
                              "Client"}
                          </span>
                        </div>

                        <span>
                          {submission.created_at
                            ? new Date(
                                submission.created_at,
                              ).toLocaleString()
                            : "—"}
                        </span>
                      </div>
                    ),
                  )}

                </div>
              ) : (
                <div className="view-doc-request-empty">
                  No submissions yet.
                </div>
              )}

            </section>

          </div>
        )}

        {/* ===============================================================
            FOOTER
        ================================================================ */}

        {formData && (
          <div className="view-doc-request-footer">

            {editing ? (
              <>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={
                    handleCancelEdit
                  }
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? "Saving changes..."
                    : "Save changes"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="secondary-btn"
                onClick={onClose}
              >
                Close
              </button>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

export default ViewDocRequest;
